import React, { useState, useEffect } from 'react';
import inflectionData from './studentInflectionData.json';

const StudentInflectionPoints = ({ program, usePng = true }) => {
  const [selectedCohort, setSelectedCohort] = useState('');
  const [cohortData, setCohortData] = useState([]);

  useEffect(() => {
    if (usePng) return; // Skip dynamic calculations when using PNG

    if (program && program.programCode) {
      const programInflection = inflectionData[program.programCode];
      if (programInflection) {
        const cohorts = Object.keys(programInflection);

        // Default to the first cohort if none selected
        if (!selectedCohort && cohorts.length > 0) {
          setSelectedCohort(cohorts[0]);
        } else if (selectedCohort) {
          // Copy data for manipulation
          const dataCopy = [...(programInflection[selectedCohort] || [])];

          // ------------------------------------------------------------
          // Force each year's total to match the previous year's continuing
          // so each next card is exactly as tall as the last year's green portion.
          // ------------------------------------------------------------
          for (let i = 1; i < dataCopy.length; i++) {
            const prev = dataCopy[i - 1];
            dataCopy[i].totalStudents = prev.continuing;
          }

          setCohortData(dataCopy);
        }
      }
    }
  }, [program, selectedCohort, usePng]);

  // Early returns for missing program data
  if (!program) return <div>No program selected.</div>;
  if (!program.programCode || !inflectionData[program.programCode]) {
    return (
      <div className="p-4 bg-white shadow-md border rounded-lg">
        No inflection data available for {program.programCode}.
      </div>
    );
  }

  // Build the cohort dropdown options
  const programInflection = inflectionData[program.programCode];
  const allCohorts = Object.keys(programInflection);

  return (
    <div className="mt-6">
      {/* Cohort Selector */}
      <div className="mb-4 flex items-center">
        <label htmlFor="cohortSelector" className="text-sm font-medium mr-2">
          Cohort:
        </label>
        <select
          id="cohortSelector"
          className="text-sm border border-gray-300 rounded px-2 py-1 min-w-[7vw]"
          value={selectedCohort}
          onChange={(e) => setSelectedCohort(e.target.value)}
        >
          {allCohorts.map((cohort) => (
            <option key={cohort} value={cohort}>
              {cohort}
            </option>
          ))}
        </select>
      </div>

      <div className="relative bg-white border rounded-lg p-4 flex">
        <img
          src="/diagram.png"
          alt="Student Inflection Diagram"
          style={{ width: '1200px', height: 'auto', alignSelf: 'flex-start' }}
        />
      </div>

    </div>
  );
};

// Extracted Dynamic Diagram rendering as a separate component for clarity.
const DynamicDiagram = ({ cohortData }) => {
  // ------------------------------------------------------------
  // Layout & scaling
  // ------------------------------------------------------------
  const maxStudents = Math.max(...cohortData.map((d) => d.totalStudents));
  const chartHeight = 400;   // total vertical space for the largest column
  const topMargin = 50;      // space above the columns
  const bottomMargin = 100;  // extra space below for waterfall shapes

  // Scale function: number of students -> pixel height
  const scale = (value) => (value / maxStudents) * chartHeight;

  // Card geometry
  const cardWidth = 120;
  const cardGap = 120;
  const getCardX = (i) => i * (cardWidth + cardGap);

  // Overall SVG dimensions
  const chartWidth =
    (cohortData.length - 1) * cardGap +
    cohortData.length * cardWidth +
    100;
  const svgHeight = chartHeight + topMargin + bottomMargin;

  // ------------------------------------------------------------
  // Build bridging polygon (green) for continuing students
  // behind the cards.
  // ------------------------------------------------------------
  function buildGreenPolygon(x1, top1, bot1, x2, top2, bot2) {
    const cornerRadius = 6;
    const startX = x1 - cornerRadius;
    const startTop = top1 + cornerRadius;
    const startBot = bot1 - cornerRadius;
    const endX = x2 + cornerRadius;
    const endTop = top2 + cornerRadius;
    const endBot = bot2 - cornerRadius;
    const curveFactor = 0.3;
    const cx1Top = startX + (endX - startX) * curveFactor;
    const cx2Top = endX - (endX - startX) * curveFactor;
    return `
      M ${startX},${startTop}
      C ${cx1Top},${startTop} ${cx2Top},${endTop} ${endX},${endTop}
      L ${endX},${endBot}
      C ${cx2Top},${endBot} ${cx1Top},${startBot} ${startX},${startBot}
      Z
    `;
  }

  // ------------------------------------------------------------
  // Build the red "waterfall" hooking off the RIGHT edge.
  // ------------------------------------------------------------
  function buildWaterfallPath(x, top, bottom) {
    const waveWidth = 20;
    const offsetRight = 60;
    const offsetDown = 30;
    const midY = (top + bottom) / 2;
    return `
      M ${x},${top}
      C ${x + waveWidth},${top} ${x + waveWidth},${midY} ${x},${midY}
      C ${x + waveWidth},${midY} ${x + offsetRight},${bottom} ${x + offsetRight},${bottom + offsetDown}
      L ${x},${bottom}
      Z
    `;
  }

  // ------------------------------------------------------------
  // Build a "transfer hook" (orange).
  // ------------------------------------------------------------
  function buildTransferHook(x, yTop, yBot, length = 40) {
    const midY = (yTop + yBot) / 2;
    const cx1Top = x + length * 0.3;
    const cx2Top = x + length * 0.7;
    return `
      M ${x},${yTop}
      C ${cx1Top},${yTop} ${cx2Top},${midY} ${x + length},${midY}
      C ${cx2Top},${midY} ${cx1Top},${yBot} ${x},${yBot}
      Z
    `;
  }

  return (
    <svg width={chartWidth} height={svgHeight} style={{ background: '#fff' }}>
      <g transform={`translate(50,0)`}>
        {cohortData.map((yearObj, i) => {
          const { year, totalStudents, continuing, dropped, transferred } = yearObj;
          const x = getCardX(i);
          const cardY = topMargin;
          const cardHeight = scale(totalStudents);
          const contH = scale(continuing);
          const contTop = cardY;
          const contBot = cardY + contH;
          const diff = dropped + transferred;
          const diffH = scale(diff);
          const diffTop = contBot;
          const diffBot = contBot + diffH;

          let x2, nextContTop, nextContBot;
          if (i < cohortData.length - 1) {
            const next = cohortData[i + 1];
            x2 = getCardX(i + 1);
            const nextContH = scale(next.continuing);
            nextContTop = topMargin;
            nextContBot = topMargin + nextContH;
          }

          let greenPolygonPath = null;
          if (i < cohortData.length - 1 && continuing > 0) {
            greenPolygonPath = buildGreenPolygon(
              x + cardWidth,
              contTop,
              contBot,
              x2,
              nextContTop,
              nextContBot
            );
          }

          let redWaterfallPath = null;
          if (diff > 0) {
            redWaterfallPath = buildWaterfallPath(x + cardWidth, diffTop, diffBot);
          }

          let transferHookPath = null;
          let transferLabelX = null;
          let transferLabelY = null;
          if (transferred > 0) {
            const hookTop = diffTop + diffH * 0.6;
            const hookBot = diffTop + diffH * 0.85;
            transferHookPath = buildTransferHook(x + cardWidth, hookTop, hookBot, 50);
            transferLabelX = x + cardWidth + 55;
            transferLabelY = (hookTop + hookBot) / 2;
          }

          const redLabelX = x + cardWidth + 8;
          const redLabelY = (diffTop + diffBot) / 2;
          let greenLabelX = null;
          let greenLabelY = null;
          if (greenPolygonPath && continuing > 0 && i < cohortData.length - 1) {
            const midX = (x + cardWidth + x2) / 2;
            const thisMidY = (contTop + contBot) / 2;
            const nextMidY = (nextContTop + nextContBot) / 2;
            greenLabelX = midX;
            greenLabelY = (thisMidY + nextMidY) / 2;
          }

          return (
            <g key={year}>
              {redWaterfallPath && (
                <>
                  <path
                    d={redWaterfallPath}
                    fill="#FEE2E2"
                    stroke="#FCA5A5"
                    strokeWidth={1}
                  />
                  {diff > 0 && (
                    <>
                      <rect
                        x={redLabelX - 16}
                        y={redLabelY - 10}
                        width={32}
                        height={20}
                        rx={10}
                        fill="#fff"
                        stroke="#FCA5A5"
                        strokeWidth={1}
                      />
                      <text
                        x={redLabelX}
                        y={redLabelY + 4}
                        fontSize={11}
                        fill="#DC2626"
                        fontWeight="500"
                        textAnchor="middle"
                      >
                        {diff}
                      </text>
                    </>
                  )}
                </>
              )}
              {transferHookPath && (
                <>
                  <path
                    d={transferHookPath}
                    fill="#FFF7ED"
                    stroke="#FDBA74"
                    strokeWidth={1}
                  />
                  <rect
                    x={transferLabelX}
                    y={transferLabelY - 10}
                    width={60}
                    height={20}
                    fill="#FFF7ED"
                    stroke="#FDBA74"
                    strokeWidth={1}
                    rx={4}
                  />
                  <text
                    x={transferLabelX + 30}
                    y={transferLabelY + 4}
                    fontSize={10}
                    fill="#FB923C"
                    fontWeight="500"
                    textAnchor="middle"
                  >
                    {transferred} Transfer
                  </text>
                </>
              )}
              {greenPolygonPath && (
                <>
                  <path
                    d={greenPolygonPath}
                    fill="#ECFDF5"
                    stroke="#34D399"
                    strokeWidth={1}
                  />
                  <text
                    x={greenLabelX}
                    y={greenLabelY}
                    fill="#059669"
                    fontSize={11}
                    fontWeight="500"
                    textAnchor="middle"
                  >
                    {continuing}
                  </text>
                </>
              )}
              <rect
                x={x}
                y={cardY}
                width={cardWidth}
                height={cardHeight}
                fill="#fff"
                stroke="#E5E7EB"
                rx={6}
                style={{
                  filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.12))'
                }}
              />
              <text
                x={x + cardWidth / 2}
                y={cardY + 18}
                textAnchor="middle"
                fontSize={12}
                fontWeight="600"
                fill="#374151"
              >
                Year {year}
              </text>
              <text
                x={x + cardWidth / 2}
                y={cardY + 34}
                textAnchor="middle"
                fontSize={11}
                fill="#4B5563"
              >
                {totalStudents} Students
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
};

export default StudentInflectionPoints;
